import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getExercise, exercisesBase } from '../../lib/exercises';
import { SITE_URL, htmlLang, type Lang } from '../../lib/i18n';
import { timerStrings, fmt, toolPath, TOOL_SLUGS, type TimerStrings } from '../../lib/tools';
import {
  DEFAULT_CONFIG,
  LIMITS,
  PRESETS,
  buildTimeline,
  formatClock,
  parseConfig,
  sanitize,
  serializeConfig,
  sessionSummary,
  stateAt,
  totalSec,
  type ArmMode,
  type PhaseKind,
  type TimerConfig,
} from '../../lib/holdTimer';
import { CopyButton } from './ToolParts';

export const TIMER_SLUG = TOOL_SLUGS.timer;

type Status = 'idle' | 'running' | 'paused' | 'done';

const PHASE_STYLE: Record<PhaseKind | 'idle' | 'done', string> = {
  idle: 'bg-surface text-ink border-line',
  prep: 'bg-[var(--pop-4-soft)] text-ink border-[var(--pop-4)]',
  hold: 'bg-[var(--brand)] text-white border-[var(--brand-edge)]',
  rest: 'bg-[var(--pop-2-soft)] text-ink border-[var(--pop-2)]',
  setRest: 'bg-[var(--pop-2-soft)] text-ink border-[var(--pop-2)]',
  done: 'bg-[var(--pop-1-soft)] text-ink border-[var(--pop-1)]',
};

// ---------------------------------------------------------------------------
// Feedback: Web Audio beeps + vibration + Wake Lock, all feature-guarded.
// ---------------------------------------------------------------------------

function useFeedback() {
  const ctxRef = useRef<AudioContext | null>(null);
  const unlock = useCallback(() => {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      if (!ctxRef.current) ctxRef.current = new AC();
      void ctxRef.current.resume();
    } catch {
      /* audio unavailable */
    }
  }, []);
  const beep = useCallback((freq: number, ms: number, times = 1) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    try {
      for (let i = 0; i < times; i++) {
        const t0 = ctx.currentTime + i * (ms / 1000 + 0.08);
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(0.35, t0 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + ms / 1000);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + ms / 1000 + 0.02);
      }
    } catch {
      /* ignore */
    }
  }, []);
  const vibrate = useCallback((pattern: number | number[]) => {
    try {
      if ('vibrate' in navigator) navigator.vibrate(pattern);
    } catch {
      /* ignore */
    }
  }, []);
  return { unlock, beep, vibrate };
}

type WakeLockSentinelLike = { release: () => Promise<void> };
function useWakeLock(active: boolean, enabled: boolean) {
  const [supported, setSupported] = useState(false);
  useEffect(() => setSupported(typeof navigator !== 'undefined' && 'wakeLock' in navigator), []);
  useEffect(() => {
    if (!active || !enabled || !supported) return;
    let lock: WakeLockSentinelLike | null = null;
    let cancelled = false;
    const acquire = async () => {
      try {
        const wl = (navigator as unknown as { wakeLock: { request: (t: 'screen') => Promise<WakeLockSentinelLike> } }).wakeLock;
        const l = await wl.request('screen');
        if (cancelled) void l.release();
        else lock = l;
      } catch {
        /* denied (e.g. battery saver) */
      }
    };
    void acquire();
    const onVis = () => {
      if (document.visibilityState === 'visible') void acquire();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      if (lock) void lock.release().catch(() => {});
    };
  }, [active, enabled, supported]);
  return supported;
}

// ---------------------------------------------------------------------------
// Stepper input
// ---------------------------------------------------------------------------

function Stepper({
  id,
  label,
  value,
  limits,
  step = 1,
  unit,
  disabled,
  onChange,
  t,
}: {
  id: string;
  label: string;
  value: number;
  limits: readonly [number, number];
  step?: number;
  unit?: string;
  disabled: boolean;
  onChange: (v: number) => void;
  t: TimerStrings;
}) {
  const set = (v: number) => onChange(Math.min(limits[1], Math.max(limits[0], v)));
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <label htmlFor={id} className="text-sm font-bold text-ink">{label}</label>
      <div className="flex items-center gap-1.5">
        <button type="button" disabled={disabled || value <= limits[0]} onClick={() => set(value - step)} aria-label={fmt(t.form.decrease, { label })} className="btn-3d btn-3d--ghost w-9 h-9 p-0 text-lg">−</button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={limits[0]}
          max={limits[1]}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => set(Number(e.target.value) || limits[0])}
          className="w-16 text-center rounded-xl border-2 border-line bg-dark text-ink font-bold py-1.5 disabled:opacity-60"
        />
        <button type="button" disabled={disabled || value >= limits[1]} onClick={() => set(value + step)} aria-label={fmt(t.form.increase, { label })} className="btn-3d btn-3d--ghost w-9 h-9 p-0 text-lg">+</button>
        {unit && <span className="text-xs text-faint w-3">{unit}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HoldTimer({ lang, embed = false }: { lang: Lang; embed?: boolean }) {
  const t = timerStrings(lang);
  const [config, setConfig] = useState<TimerConfig>(DEFAULT_CONFIG);
  const [preset, setPreset] = useState<string | null>('pronation');
  const [status, setStatus] = useState<Status>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [sound, setSound] = useState(true);
  const [vib, setVib] = useState(true);
  const [awake, setAwake] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [today, setToday] = useState('');
  const accRef = useRef(0);
  const startRef = useRef(0);
  const { unlock, beep, vibrate } = useFeedback();
  const wakeSupported = useWakeLock(status === 'running', awake);

  // Initial: preset default, or the share URL.
  useEffect(() => {
    setToday(new Intl.DateTimeFormat(htmlLang[lang], { dateStyle: 'medium' }).format(new Date()));
    const search = new URLSearchParams(window.location.search);
    if (search.has('hold') || search.has('preset')) {
      const parsed = parseConfig(search);
      setConfig(parsed.config);
      setPreset(parsed.preset);
      setDirty(true);
    } else {
      const p = PRESETS.find((x) => x.id === 'pronation')!;
      setConfig(sanitize({ ...DEFAULT_CONFIG, ...p.config }));
    }
  }, [lang]);

  const timeline = useMemo(() => buildTimeline(config), [config]);
  const state = stateAt(timeline, elapsedMs);
  const summary = useMemo(() => sessionSummary(config), [config]);
  const query = serializeConfig(config, preset);

  useEffect(() => {
    if (!dirty) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${query}`);
  }, [query, dirty]);

  // Clock: 10 Hz is plenty for a whole-second display and cheap on battery.
  useEffect(() => {
    if (status !== 'running') return;
    const id = window.setInterval(() => {
      const ms = accRef.current + (performance.now() - startRef.current);
      setElapsedMs(ms);
    }, 100);
    return () => window.clearInterval(id);
  }, [status]);

  // Phase changes + countdown beeps.
  const lastPhase = useRef(-2);
  const lastSec = useRef(-1);
  useEffect(() => {
    if (status !== 'running' && !(status === 'done' && lastPhase.current !== -1)) return;
    if (state.done) {
      if (lastPhase.current !== -1) {
        lastPhase.current = -1;
        if (sound) beep(880, 600, 1);
        if (vib) vibrate([300, 120, 300, 120, 600]);
        setStatus('done');
      }
      return;
    }
    if (state.index !== lastPhase.current) {
      lastPhase.current = state.index;
      lastSec.current = state.remainingSec;
      const k = state.phase!.kind;
      if (k === 'hold') {
        if (sound) beep(988, 450, 1);
        if (vib) vibrate(400);
      } else if (k === 'rest' || k === 'setRest') {
        if (sound) beep(523, 180, 2);
        if (vib) vibrate([150, 100, 150]);
      }
      return;
    }
    if (state.remainingSec !== lastSec.current) {
      lastSec.current = state.remainingSec;
      if (state.remainingSec <= 3 && sound) beep(state.phase!.kind === 'hold' ? 660 : 740, 90, 1);
    }
  }, [state.index, state.remainingSec, state.done, state.phase, status, sound, vib, beep, vibrate]);

  const start = () => {
    unlock();
    if (status === 'done') {
      accRef.current = 0;
      setElapsedMs(0);
    }
    lastPhase.current = -2;
    startRef.current = performance.now();
    setStatus('running');
  };
  const pause = () => {
    accRef.current += performance.now() - startRef.current;
    setElapsedMs(accRef.current);
    setStatus('paused');
  };
  const reset = () => {
    accRef.current = 0;
    setElapsedMs(0);
    lastPhase.current = -2;
    setStatus('idle');
  };
  const skip = () => {
    if (!state.next) {
      accRef.current = totalSec(timeline) * 1000;
    } else accRef.current = state.next.startSec * 1000;
    startRef.current = performance.now();
    setElapsedMs(accRef.current);
  };

  const update = (patch: Partial<TimerConfig>, keepPreset = false) => {
    setConfig((c) => sanitize({ ...c, ...patch }));
    if (!keepPreset) setPreset(null);
    setDirty(true);
    reset();
  };
  const loadPreset = (id: string) => {
    const p = PRESETS.find((x) => x.id === id)!;
    setConfig((c) => sanitize({ ...c, ...p.config }));
    setPreset(id);
    setDirty(true);
    reset();
  };

  const locked = status === 'running' || status === 'paused';
  const phaseKey: PhaseKind | 'idle' | 'done' = status === 'idle' ? 'idle' : state.done ? 'done' : state.phase!.kind;
  const showPhase = status === 'idle' ? timeline[0] : state.phase;
  const big = status === 'idle' ? (timeline[0]?.durationSec ?? 0) : state.remainingSec;
  const presetEx = preset ? getExercise(lang, PRESETS.find((p) => p.id === preset)!.slug) : undefined;
  const label = (k: PhaseKind | 'idle' | 'done') => (t.display as Record<string, unknown>)[k] as string;
  const shareUrl = () => `${SITE_URL}${toolPath(lang, TIMER_SLUG)}?${query}`;
  const armModes: ArmMode[] = ['alternate', 'right', 'left'];

  return (
    <div className={`grid gap-6 ${embed ? 'md:grid-cols-[1fr_320px]' : 'lg:grid-cols-[1fr_360px]'} items-start`}>
      {/* Display */}
      <div className="min-w-0">
        <div
          className={`rounded-[2rem] border-4 p-5 sm:p-8 text-center transition-colors print:hidden ${PHASE_STYLE[phaseKey]}`}
          role="timer"
          aria-label={t.h1}
        >
          <p className="font-heading font-black uppercase tracking-widest text-lg sm:text-2xl" aria-live="polite">
            {label(phaseKey)}
            {showPhase?.arm && !state.done && <span className="block text-base sm:text-xl normal-case tracking-normal opacity-90 mt-1">{t.display.arms[showPhase.arm]}</span>}
          </p>
          <p className="font-heading font-black tabular-nums leading-none my-3 sm:my-5 text-[6.5rem] sm:text-[10rem]">
            {state.done && status === 'done' ? '✓' : big}
          </p>
          {showPhase && !state.done && (
            <p className="text-sm sm:text-base font-bold opacity-90">
              {fmt(t.display.position, { set: showPhase.set, sets: config.sets, round: showPhase.round, rounds: config.rounds })}
            </p>
          )}
          <div className="mt-4 h-2.5 rounded-full bg-black/10 overflow-hidden" aria-hidden="true">
            <div className="h-full bg-current origin-left" style={{ transform: `scaleX(${status === 'idle' ? 0 : state.phaseProgress})` }} />
          </div>
          <div className="mt-3 flex justify-between text-xs sm:text-sm font-bold opacity-80 tabular-nums">
            <span>{t.display.elapsed} {formatClock(state.elapsedSec)}</span>
            <span>{state.next && !state.done ? fmt(t.display.next, { phase: label(state.next.kind) + (state.next.arm && state.next.kind === 'hold' ? ` · ${t.display.arms[state.next.arm]}` : '') }) : ''}</span>
            <span>{t.display.total} {formatClock(summary.totalSec)}</span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap justify-center gap-2 print:hidden">
          {status === 'running' ? (
            <button type="button" onClick={pause} className="btn-3d btn-3d--lg px-8 min-w-[9rem]">⏸ {t.controls.pause}</button>
          ) : (
            <button type="button" onClick={start} className="btn-3d btn-3d--lg px-8 min-w-[9rem]">
              ▶ {status === 'paused' ? t.controls.resume : t.controls.start}
            </button>
          )}
          <button type="button" onClick={skip} disabled={status === 'idle' || status === 'done'} className="btn-3d btn-3d--ghost btn-3d--lg px-6">⏭ {t.controls.skip}</button>
          <button type="button" onClick={reset} disabled={status === 'idle'} className="btn-3d btn-3d--ghost btn-3d--lg px-6">↺ {t.controls.reset}</button>
        </div>

        {/* Summary (printable) */}
        <section className="card-float p-5 sm:p-6 mt-6 break-inside-avoid" aria-labelledby="timer-summary">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 id="timer-summary" className="text-xl font-extrabold text-ink">{t.summary.heading}</h2>
            <div className="flex flex-wrap gap-2 print:hidden">
              <CopyButton getText={shareUrl} label={`🔗 ${t.summary.copyLink}`} copiedLabel={t.summary.copied} />
              <button type="button" onClick={() => window.print()} className="btn-3d btn-3d--ghost btn-3d--sm px-4 py-2">🖨️ {t.summary.print}</button>
            </div>
          </div>
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {today && (
              <div className="flex justify-between gap-3 border-b border-line py-1.5"><dt className="text-faint">{t.summary.date}</dt><dd className="font-bold text-ink">{today}</dd></div>
            )}
            {presetEx && (
              <div className="flex justify-between gap-3 border-b border-line py-1.5">
                <dt className="text-faint">{t.summary.exercise}</dt>
                <dd className="font-bold text-ink text-right"><a href={`${exercisesBase(lang)}/${presetEx.slug}`} className="hover:underline">{presetEx.t.name}</a></dd>
              </div>
            )}
            <div className="flex justify-between gap-3 border-b border-line py-1.5">
              <dt className="text-faint">{t.summary.planned}</dt>
              <dd className="font-bold text-ink text-right">{fmt(t.summary.setup, { sets: config.sets, rounds: config.rounds, hold: config.holdSec, rest: config.restSec })} · {t.form.armOptions[config.arm]}</dd>
            </div>
            <div className="flex justify-between gap-3 border-b border-line py-1.5"><dt className="text-faint">{t.summary.totalTime}</dt><dd className="font-bold text-ink">{formatClock(summary.totalSec)}</dd></div>
            <div className="flex justify-between gap-3 border-b border-line py-1.5"><dt className="text-faint">{t.summary.holds}</dt><dd className="font-bold text-ink">{fmt(t.summary.holdsValue, { right: summary.holdsRight, left: summary.holdsLeft })}</dd></div>
            <div className="flex justify-between gap-3 border-b border-line py-1.5"><dt className="text-faint">{t.summary.tut}</dt><dd className="font-bold text-ink">{fmt(t.summary.tutValue, { right: summary.tutPerArmSec.right, left: summary.tutPerArmSec.left })}</dd></div>
            {status !== 'idle' && (
              <div className="flex justify-between gap-3 border-b border-line py-1.5 sm:col-span-2"><dt className="text-faint">{t.summary.completed}</dt><dd className="font-bold text-ink">{fmt(t.summary.completedValue, { holds: state.holdsCompleted, tut: state.timeUnderTensionSec })}</dd></div>
            )}
          </dl>
        </section>
      </div>

      {/* Settings */}
      <form className="card-float p-5 sm:p-6 print:hidden" onSubmit={(e) => e.preventDefault()} aria-label={t.form.heading}>
        <h2 className="text-xl font-extrabold text-ink mb-3">{t.form.heading}</h2>
        <fieldset className="mb-4">
          <legend className="text-sm font-black text-ink mb-1">{t.form.presets}</legend>
          <p className="text-xs text-faint mb-2">{t.form.presetHint}</p>
          <ul className="grid gap-1.5">
            {PRESETS.map((p) => {
              const ex = getExercise(lang, p.slug);
              if (!ex) return null;
              const active = preset === p.id;
              return (
                <li key={p.id} className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={locked}
                    aria-pressed={active}
                    onClick={() => loadPreset(p.id)}
                    className={`flex-1 text-left rounded-2xl border-2 px-3 py-2 text-sm font-bold transition-colors disabled:opacity-60 ${active ? 'border-[var(--brand)] bg-[var(--brand-soft)] text-[var(--brand-ink)]' : 'border-line bg-surface text-ink hover:border-[var(--brand)]'}`}
                  >
                    {ex.t.name}
                    <span className="block text-xs font-semibold text-faint">{p.config.rounds} × {p.config.holdSec} {t.form.sec}</span>
                  </button>
                  <a href={`${exercisesBase(lang)}/${ex.slug}`} className="text-xs font-bold text-[var(--brand-ink)] hover:underline whitespace-nowrap">{t.form.howTo} →</a>
                </li>
              );
            })}
          </ul>
        </fieldset>

        <Stepper id="t-hold" label={t.form.hold} value={config.holdSec} limits={LIMITS.holdSec} unit={t.form.sec} disabled={locked} onChange={(v) => update({ holdSec: v })} t={t} />
        <Stepper id="t-rest" label={t.form.rest} value={config.restSec} limits={LIMITS.restSec} step={5} unit={t.form.sec} disabled={locked} onChange={(v) => update({ restSec: v })} t={t} />
        <Stepper id="t-rounds" label={t.form.rounds} value={config.rounds} limits={LIMITS.rounds} disabled={locked} onChange={(v) => update({ rounds: v })} t={t} />
        <Stepper id="t-sets" label={t.form.sets} value={config.sets} limits={LIMITS.sets} disabled={locked} onChange={(v) => update({ sets: v })} t={t} />
        {config.sets > 1 && (
          <Stepper id="t-setrest" label={t.form.setRest} value={config.setRestSec} limits={LIMITS.setRestSec} step={15} unit={t.form.sec} disabled={locked} onChange={(v) => update({ setRestSec: v })} t={t} />
        )}
        <Stepper id="t-prep" label={t.form.prep} value={config.prepSec} limits={LIMITS.prepSec} unit={t.form.sec} disabled={locked} onChange={(v) => update({ prepSec: v }, true)} t={t} />

        <fieldset className="mt-3">
          <legend className="text-sm font-black text-ink mb-2">{t.form.arm}</legend>
          <div className="flex flex-wrap gap-2">
            {armModes.map((a) => (
              <label key={a} className={`cursor-pointer rounded-full border-2 px-3.5 py-1.5 text-sm font-bold has-[:focus-visible]:ring-4 has-[:focus-visible]:ring-[var(--brand-soft)] ${config.arm === a ? 'border-[var(--brand)] bg-[var(--brand)] text-white' : 'border-line bg-surface text-muted'} ${locked ? 'opacity-60' : ''}`}>
                <input type="radio" name="t-arm" className="sr-only" checked={config.arm === a} disabled={locked} onChange={() => update({ arm: a }, true)} />
                {t.form.armOptions[a]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-4 grid gap-1.5 text-sm">
          <label className="flex items-center gap-2.5 cursor-pointer"><input type="checkbox" checked={sound} onChange={(e) => setSound(e.target.checked)} className="w-4 h-4 accent-primary" /> 🔊 {t.form.sound}</label>
          <label className="flex items-center gap-2.5 cursor-pointer"><input type="checkbox" checked={vib} onChange={(e) => setVib(e.target.checked)} className="w-4 h-4 accent-primary" /> 📳 {t.form.vibrate}</label>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={awake && wakeSupported} disabled={!wakeSupported} onChange={(e) => setAwake(e.target.checked)} className="w-4 h-4 accent-primary" />
            ☀️ {t.form.keepAwake}
            {!wakeSupported && <span className="text-xs text-faint">({t.form.unsupported})</span>}
          </label>
        </div>
      </form>
    </div>
  );
}

export function TimerSafety({ lang }: { lang: Lang }) {
  const t = timerStrings(lang);
  return (
    <section aria-labelledby="timer-safety" className="mb-14 rounded-3xl border-2 border-[var(--pop-4)] bg-[var(--pop-4-soft)] p-5 sm:p-6 break-inside-avoid">
      <h2 id="timer-safety" className="text-xl font-extrabold text-ink mb-3">⚠️ {t.safety.heading}</h2>
      <ul className="space-y-2 text-sm text-ink/90 list-disc pl-5">
        {t.safety.items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </section>
  );
}
